import { FixtureGameHistoryProvider, FixtureReplayRoundProvider, loadLocalizationDictionaryFromBundle, type GameDefinition } from "@slot/game-shell";
import { version } from "../package.json";
import { BOOT_FALLBACK_GRID } from "./composition/bootFallbackGrid";
import { createPresentation } from "./composition/createPresentation";
import { installCoinHoldHook } from "./dev/installCoinHoldHook";
import { createFeaturePurchaseOffers } from "./feature/createFeaturePurchaseOffers";
import { GRID_SIZE } from "./gridSize";
import { INITIAL_LINES, LINE_OPTIONS } from "./rules/lines";
import { gameEnDictionary } from "./localization/game.en";
import { createScene, type CoinHoldScene } from "./scene/createScene";
import { FIXTURE_SCRIPTS } from "./sim/fixtureScripts";
import { BIG_WIN_TIERS, BONUS_BUYS } from "./sim/offlineMath";
import { CoinHoldIntro } from "./views/intro/CoinHoldIntro";
import { OFFLINE_BALANCE_MINOR, OFFLINE_CURRENCY, createFixtureCatalog, createHistoryFixture, createReplayRounds } from "./sim/offlineData";
import { CoinHoldOfflineServer } from "./sim/OfflineServer";

/**
 * coin-hold as one object (plan §2.2): what the shell's boot, chrome and offline data take from this title, and the
 * factories for its scene and its round presentation.
 */
export const coinHold: GameDefinition<CoinHoldScene> = {
  id: "coin-hold",
  // The package's own version (games/coin-hold/package.json), so a release bumps one number.
  version,
  grid: GRID_SIZE,
  bootFallbackGrid: BOOT_FALLBACK_GRID,

  boot: {
    locale: {
      baseDictionary: gameEnDictionary,
      loadRemoteDictionary: ({ locale, gameBundleRegistry }) => loadLocalizationDictionaryFromBundle(gameBundleRegistry, `localization/game.${locale}`),
    },
    // The offline boot's operator settings: the title's name, the lines it offers (decision 56: the ladder is a line's
    // bet), the buys' prices, the big-win tiers (all the server's online). The game has no gamble.
    fixtureSettingsOverrides: {
      gameTitle: "Coin Hold",
      betLineOptions: [...LINE_OPTIONS],
      initialBetLines: INITIAL_LINES,
      gambleEnabled: false,
      featurePriceMultipliersById: Object.fromEntries(Object.entries(BONUS_BUYS).map(([id, buy]) => [id, buy.price])),
      bigWinTiers: BIG_WIN_TIERS.map((tier) => ({ ...tier })),
    },
    // The shell loads its core set (`shared-core`, `game-shared-visuals`, `shell-ui`) itself; the title has no bundle of its own.
    extraBundles: [],
    // No sounds yet: the shell's mapping plays its own bank's ids, and the title registers none.
    audioOverrides: null,
  },

  // The designer's backgrounds (2026-10-11), a still per orientation (decision 69): the shell shows them on the loader,
  // the intro and under the scene, drawn in the canvas (the sparkles' and the flames' additive light lands on them).
  background: { stills: { portrait: "background-portrait", landscape: "background-landscape" } },

  chrome: {
    // The rules page states the rules' numbers in its own words; the title adds no tokens of its own.
    buildExtraInfoTokens: () => ({}),
    featurePurchaseOffers: createFeaturePurchaseOffers(),
    // The bar's Buy Bonus as the mock-up's red disc in a gold ring, shown as drawn (decision 49); the shell's black label
    // fitted inside the red face, clear of the ring (decision 52).
    controlArt: { bundle: "game-shared-visuals", atlas: "sprites/controlArt", ownColours: ["buyBonusBtn"], buyBonusLabel: { width: 0.74, height: 0.5 } },
    // The bonus shows its respins on its meter and its total in the bar's Win, as the mock-up does: no feature HUD in the
    // bar, so the Spin button keeps its place through the bonus, where every respin waits for it (decision 55).
    featureHud: false,
  },

  fixtures: {
    createCatalog: () => createFixtureCatalog(),
    // Every build without a backend: the title's offline server (its rules at the request's bet; the dev menu's and the
    // e2e suite's scripted rounds through its playlist).
    createRoundProvider: () =>
      new CoinHoldOfflineServer({
        fixtures: FIXTURE_SCRIPTS,
        rng: Math.random,
        startingBalanceMinor: OFFLINE_BALANCE_MINOR,
        currencyCode: OFFLINE_CURRENCY,
        simulatedLatencyMs: 50,
        sessionStamp: Math.floor(Math.random() * 1e9).toString(36),
      }),
    createHistoryProvider: () => new FixtureGameHistoryProvider(createHistoryFixture(), { simulatedLatencyMs: 300 }),
    createReplayRoundProvider: () => new FixtureReplayRoundProvider(createReplayRounds(), { simulatedLatencyMs: 300 }),
  },

  createScene,
  createPresentation,
  // Its own intro in the canvas (decision 77), after 3 Coin Volcanoes': the boosters showing what they do on the game's
  // own pieces; the shell's splash keeps its prompt and its badges over it.
  createIntro: (scene, deps) => new CoinHoldIntro({ assets: scene.assets, ...deps }),
  dev: {
    // The board, the pots, the multipliers, the meter and the grid's growth beside the shell's members, for the e2e suite.
    installHook: installCoinHoldHook,
  },
};
